import moment, { type Moment } from "moment";
import { TeamData } from "./OrgTypes";
import Visitable from "../../../shared/interfaces/visitable.ts";
import useTeamCan, { TeamFeature } from "../../Auth/useTeamCan.ts";
import { ChecksPermissions } from "../../../shared/interfaces/ChecksPermissions.ts";
import { ref } from "vue";

export interface Team extends TeamData, Visitable<Team>, ChecksPermissions<TeamFeature> {
    organizationSlug: string,
    setFeatures: (features?: TeamFeature[]) => Team
    // Materialized teams always convert created_at with moment() (see below)
    created_at: Moment
}


/**
 * the default behaviour when you call the visit method
 */
const VisitTeam = (team: Team, query = {}) => {
    const theRouter = window.$router;

    theRouter.push({
        name: "org.team",
        query,
        params: {
            org: team.organizationSlug,
            team: team.slug,
        },
    });
};

/**
 * Takes Team data from the server and converts it into an object with
 * methods that we can use to interact with it.
 */
export default function TeamModel(
    organizationSlug: string,
    data: TeamData,
): Team {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- query bag mirrors the untyped router visit payload
    let visitOverride: ((model: Team, query?: Record<string, any>) => void) = VisitTeam;

    const that = {
        // The slug of the org that owns this team
        organizationSlug,

        // The data sent from the server
        ...data,
        // name: data.name,
        // slug: data.slug,
        // abbreviation: data.abbreviation,
        // color: data.color,
        created_at: moment(data.created_at),
        // domains: data.domains,
        // blacklisted: data.blacklisted,
        // membership: data.membership,
        // features: data.features,
        // options: data.options,

        /**
         * Redirect the browser to the show page for this team
         * @param query optional query string params to add
         */
        visit: function (query = {}) {
            visitOverride(that, query);
        },

        /**
         * Returns true if the current user has permission to perform the provided action on this team
         * Functionally equivalent to calling useTeamCan composable with only this team
         */
        can: (name: TeamFeature | TeamFeature[]): boolean => {
            return useTeamCan({ team: ref(that) }).can(name);
        },

        /**
         * Allows you to set the features that are set on this object
         */
        setFeatures: (features: TeamFeature[] = []): Team => {
            that.features = features;
            return that;
        },

        /**
         * Allows you to set the function that is called when you call the visit method
         */
        setVisit(callback = (model: Team, query = {}) => {
        }): Team {
            visitOverride = callback;
            return that;
        },
    };

    //
    // PERMISSIONS
    // Currently these are hard coded, any user in a team is allowed to view analytics and urls.
    // In the future we would like to move this to the backend and pass it to the front 
    that.features.push("view_analytics");
    that.features.push("view_urls");

    return that;
}
